// client/src/lib/lazyWithRetry.ts
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/** One retry after a short wait: a chunk fetch that raced an OTA swap / flaky network usually succeeds. */
export const LAZY_RETRY_DELAY_MS = 500;

/** The loader behind lazyWithRetry — exported for the tests. */
export async function retryImport<T>(
  factory: () => Promise<T>,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<T> {
  try {
    return await factory();
  } catch {
    await sleep(LAZY_RETRY_DELAY_MS);
    return factory(); // a second failure propagates to the nearest error boundary
  }
}

/**
 * React.lazy that retries a failed dynamic import once (Sentry LAMIRAGECO-DV).
 *
 * ⚠️ The retry is a cheap best effort, NOT the recovery: WebKit (and some Chromium builds) cache a
 * failed module import for the life of the document, so the second import() of the same specifier
 * can reject instantly with the same error. The real recovery is the reload — client-app's
 * vite:preloadError handler and the ErrorBoundary (lib/chunkReloadGuard).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => retryImport(factory));
}
